import { SourceTypeConfigHelper } from '../../create-pipeline/components/source-settings-form/config-helper';
import PipelinesAPI from '../../api/PipelinesAPI';
import { _teamSettingsService } from '../../../../core/services/team-settings.service';

export class YoutubeAnalyticsConfigHelper implements SourceTypeConfigHelper {
  isYoutubeContentOwnerReportEnabled = _teamSettingsService?.getTeamSettingsValue()?.['add_dropdown_to_select_content_owner_yta'] ?? false;
  static convertReportListToGroups(reports: any[]) {
    return reports
      .sort((a, b) => a.group.localeCompare(b.group))
      .map(option => ({
        ...option,
        category: option.group
      }));
  }

  static async metDataResolverWithoutContentOwnerSupport(authData: any) {
    const { data } = await PipelinesAPI.getYoutubeChannelReportList(authData.oauthTokenId);

    return {
      channelReportList: YoutubeAnalyticsConfigHelper.convertReportListToGroups(data.reports)
    };
  }

  static async metDataResolverWithContentOwnerSupport(authData: any, rawData: any) {
    const selectedContentOwners = rawData.content_owners;

    const getPromises = () => {
      return [
        PipelinesAPI.getYoutubeChannelReportList(authData.oauthTokenId).then(
          res => res.data.reports
        ),
        PipelinesAPI.getYoutubeContentOwnerList(authData.oauthTokenId).then(
          res => res.data.content_owners
        ),
        ...(selectedContentOwners?.length
          ? [
            PipelinesAPI.getYAContentOwnerReportList(
              authData.oauthTokenId,
              selectedContentOwners
            ).then(res => res.data.reports)
          ]
          : [])
      ];
    };

    const [rawChannelReports, contentOwnerList, rawContentOwnersReports] = await Promise.all(
      getPromises()
    );

    const channelReportList =
      YoutubeAnalyticsConfigHelper.convertReportListToGroups(rawChannelReports);

    let contentOwnerReportList;

    if (rawContentOwnersReports) {
      contentOwnerReportList = rawContentOwnersReports;
    } else if (contentOwnerList?.length) {
      contentOwnerReportList = await PipelinesAPI.getYAContentOwnerReportList(
        authData.oauthTokenId,
        contentOwnerList.map(owner => owner.id)
      ).then(res => res.data.reports);
    }

    if (contentOwnerReportList) {
      contentOwnerReportList =
        YoutubeAnalyticsConfigHelper.convertReportListToGroups(contentOwnerReportList);
    }

    return {
      channelReportList,
      contentOwnerList,
      contentOwnerReportList
    };
  }

  getConfigFromRawData(rawData: any, metaData: any) {
    const config: {
      channelReports: any[];
      contentOwners?: any[];
      contentOwnerReports?: any[];
    } = {
      channelReports: []
    };

    if (rawData.channel_reports) {
      config.channelReports = this.getListFromRawData(
        metaData.channelReportList,
        rawData.channel_reports || []
      );
    }

    if (this.isYoutubeContentOwnerReportEnabled) {
      if (rawData.content_owners) {
        config.contentOwners = this.getListFromRawData(
          metaData.contentOwnerList,
          rawData.content_owners
        );
      }

      if (rawData.content_owner_reports) {
        config.contentOwnerReports = this.getListFromRawData(
          metaData.contentOwnerReportList,
          rawData.content_owner_reports
        );
      }
    }

    return config;
  }

  getDefaultConfig(metaData: any) {
    return {
      channelReports: metaData.channelReportList,
      ...(this.isYoutubeContentOwnerReportEnabled
        ? {
          contentOwners: metaData.contentOwnerList,
          contentOwnerReports: metaData.contentOwnerReportList
        }
        : {})
    };
  }

  buildConnectionParams(data: any) {
    return {
      channel_reports: (data.channelReports || []).map(report => report.id),
      ...(this.isYoutubeContentOwnerReportEnabled
        ? {
          content_owners: (data.contentOwners || []).map(owner => owner.id),
          content_owner_reports: (data.contentOwnerReports || []).map(report => report.id)
        }
        : {
          content_owners: []
        })
    };
  }

  async metaDataResolver(sourceType: string, authData: any, rawData: any) {
    if (this.isYoutubeContentOwnerReportEnabled) {
      return await YoutubeAnalyticsConfigHelper.metDataResolverWithContentOwnerSupport(
        authData,
        rawData
      );
    }

    return await YoutubeAnalyticsConfigHelper.metDataResolverWithoutContentOwnerSupport(authData);
  }

  getListFromRawData(referenceList: { id: string }[], rawDataList: string[]) {
    return rawDataList.map(id => referenceList.find(obj => id === obj.id));
  }
}
